import React from "react";
import { Link, Route, Routes, useParams, Navigate } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { ArrowBackIcon } from "../../util/Icons";
import SubMenuWrapper, { SubMenuItem } from "../../util/SubMenu";
import WeekPage from "./WeekPage";

// Import program data
import strengthTrainingData from "../data/strengthTrainingData.json";
import mobilityProgramData from "../data/mobilityProgramData.json";
import oneHundredExercisesData from "../data/oneHundredExercises.json";
import strengthForLifeData from "../data/strengthForLifeData";

// Map program IDs to their data
const PROGRAM_DATA_MAP = {
    "strength-training": strengthTrainingData,
    "strength-for-life": strengthForLifeData,
    "oneHundred": oneHundredExercisesData,
    "mobility": mobilityProgramData,
};

const MEMBER_ONLY_PROGRAMS = new Set(["strength-training", "strength-for-life", "oneHundred", "mobility"]);

/**
 * ProgramPage - Generic program renderer
 * Reads programId from URL and loads corresponding JSON data
 */
export default function ProgramPage() {
    const { programId } = useParams();
    const { membership } = useUserContext();
    const programData = PROGRAM_DATA_MAP[programId];
    const isMember = membership === true;
    const isProtectedProgram = MEMBER_ONLY_PROGRAMS.has(programId);

    if (!programData) {
        return (
            <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: '#ffffff' }}>
                <div className="max-w-4xl mx-auto">
                    <SubMenuWrapper>
                        <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
                    </SubMenuWrapper>
                    <div className="text-center mt-8">
                        <span className="text-6xl mb-4 block">🔍</span>
                        <h2 className="text-2xl font-bold mb-4" style={{ color: '#1e293b' }}>
                            Program Not Found
                        </h2>
                        <p className="text-base" style={{ color: '#64748b' }}>
                            The program you're looking for doesn't exist.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (isProtectedProgram && !isMember) {
        return (
            <div className="min-h-screen py-8 px-2 md:px-4" style={{ background: "#ffffff" }}>
                <div className="max-w-4xl mx-auto">
                    <SubMenuWrapper>
                        <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
                    </SubMenuWrapper>

                    <div className="text-center mt-8">
                        <div
                            className="rounded-3xl p-8"
                            style={{
                                background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
                                border: "1px solid rgba(0, 107, 193, 0.14)",
                            }}
                        >
                            <span className="text-6xl mb-4 block">🔒</span>
                            <h2 className="text-2xl font-bold mb-4" style={{ color: "#1e293b" }}>
                                Members Only Program
                            </h2>
                            <p className="text-base mb-6" style={{ color: "#64748b" }}>
                                {programData.title} is available only to Momentum Members.
                            </p>
                            <Link
                                to="/membership"
                                className="inline-block rounded-2xl px-8 py-4 font-bold text-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl no-underline"
                                style={{
                                    background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)",
                                    color: "white",
                                    boxShadow: "0 10px 40px rgba(0, 107, 193, 0.24)",
                                }}
                            >
                                Become a Member
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const weeks = programData.weeks || [];
    const overviewWeeks = programId === "strength-for-life"
        ? weeks.filter((week) => week.id !== "resistance-bands")
        : weeks;
    const hasFeatureWeekCards = overviewWeeks.some((week) => week.cardStyle === "feature");
    const showStrengthForLifeDisclaimer = programId === "strength-for-life";

    return (
        <Routes>
            {/* Program Overview - Week Cards */}
            <Route path="/" element={
                <div className="min-h-screen py-6 px-2 md:py-8 md:px-4" style={{ background: '#ffffff' }}>
                    <div className="max-w-4xl mx-auto">
                        {/* SubMenu */}
                        <SubMenuWrapper>
                            <SubMenuItem icon={<ArrowBackIcon size={24} color="#6b7280" />} label={"Health Hub"} to={"/strong-healthy"} />
                        </SubMenuWrapper>

                        {/* Hero Section */}
                        <div className="text-center mb-6 mt-4">
                            {programData.badge && (
                                <span className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-3" style={{
                                    background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                                    color: 'white'
                                }}>
                                    {programData.badge}
                                </span>
                            )}
                            {programData.title && (
                                <h1 className="text-3xl md:text-4xl font-extrabold mb-3" style={{
                                    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}>
                                    {programData.title}
                                </h1>
                            )}
                            {programData.subtitle && (
                                <p className="text-base md:text-lg" style={{ color: '#64748b' }}>
                                    {programData.subtitle}
                                </p>
                            )}
                        </div>

                        {/* Program Overview Card */}
                        {programData.overview && (
                            <div className="rounded-3xl p-4 sm:p-5 md:p-8 mb-5 md:mb-6" style={{
                                background: 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                                border: '1px solid rgba(99, 102, 241, 0.15)'
                            }}>
                                {programData.overview.title && (
                                    <h3 className="text-xl font-bold mb-4" style={{ color: '#1e293b' }}>
                                        {programData.overview.title}
                                    </h3>
                                )}
                                {programData.overview.description && (
                                    <p className="text-base m-0" style={{ color: '#64748b' }}>
                                        {programData.overview.description}
                                    </p>
                                )}
                                {programData.overview.video?.src && (
                                    <div
                                        className="mt-5 overflow-hidden rounded-3xl"
                                        style={{
                                            aspectRatio: "16 / 9",
                                            border: "1px solid rgba(99, 102, 241, 0.12)",
                                            boxShadow: "0 20px 45px rgba(15, 23, 42, 0.08)",
                                        }}
                                    >
                                        <iframe
                                            src={programData.overview.video.src}
                                            className="w-full h-full"
                                            frameBorder="0"
                                            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            allowFullScreen
                                            title={programData.overview.video.title || `${programData.title} overview video`}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Weeks Grid */}
                        {overviewWeeks.length > 0 ? (
                            <div className={hasFeatureWeekCards ? "grid gap-3 md:gap-4 md:grid-cols-2 xl:grid-cols-3" : "grid gap-3"}>
                                {overviewWeeks.map((week) => (
                                    <WeekCard key={week.id} week={week} programId={programId} />
                                ))}
                            </div>
                        ) : (
                            <div className="rounded-3xl p-4 sm:p-5 md:p-8 text-center" style={{
                                background: 'linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)',
                                border: '1px solid rgba(99, 102, 241, 0.15)'
                            }}>
                                <p style={{ color: '#64748b' }}>No content available yet.</p>
                            </div>
                        )}

                        {showStrengthForLifeDisclaimer && (
                            <div
                                className="rounded-3xl px-4 py-4 sm:px-5 md:px-6 mt-5 md:mt-6"
                                style={{
                                    background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
                                    border: "1px solid rgba(249, 115, 22, 0.18)",
                                }}
                            >
                                <p className="text-sm md:text-base m-0" style={{ color: "#7c2d12", lineHeight: 1.7 }}>
                                    Disclaimer: You participate in these programs at your own risk. My Challenge Canada is not
                                    responsible or liable for any injury, loss, or damage that may result from using these workouts.
                                    Stop if something feels wrong, and speak with a qualified healthcare professional before starting
                                    if you have any medical concerns, pain, or pre-existing conditions.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            } />

            {/* Week Detail Pages */}
            <Route path="/:weekId" element={<WeekPageWrapper programData={programData} />} />

            {/* Fallback */}
            <Route path="/*" element={<Navigate to={`/strong-healthy/${programId}`} />} />
        </Routes>
    );
}

/**
 * WeekPageWrapper - Finds week data and passes to WeekPage
 */
function WeekPageWrapper({ programData }) {
    const { weekId } = useParams();
    const weeks = programData.weeks || [];
    const weekData = weeks.find(w => w.id === weekId);

    return <WeekPage weekData={weekData} programData={programData} />;
}

/**
 * WeekCard - Clickable card that navigates to week detail page
 */
function WeekCard({ week, programId }) {
    const exerciseCount = week.exercises?.length || 0;
    const singularLabel = week.itemLabelSingular || "topic";
    const pluralLabel = week.itemLabelPlural || `${singularLabel}s`;
    const isFeatureCard = week.cardStyle === "feature";

    if (isFeatureCard) {
        return (
            <Link
                to={`/strong-healthy/${programId}/${week.id}`}
                className="no-underline block h-full"
            >
                <div
                    className="rounded-[28px] p-4 sm:p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl cursor-pointer h-full min-h-[240px] sm:min-h-[280px] lg:min-h-[320px] flex flex-col overflow-hidden"
                    style={{
                        background: week.cardGradient || "linear-gradient(135deg, #f1f5f9 0%, #e0e7ff 100%)",
                        border: `1px solid ${week.cardBorderColor || "rgba(99, 102, 241, 0.15)"}`,
                    }}
                >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mb-3 md:mb-4" style={{
                        background: "rgba(255, 255, 255, 0.4)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35)"
                    }}>
                        {week.icon || "💪"}
                    </div>

                    {week.eyebrow && (
                        <span className="inline-flex w-fit px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold mb-3" style={{
                            background: "rgba(15, 23, 42, 0.08)",
                            color: "#334155"
                        }}>
                            {week.eyebrow}
                        </span>
                    )}

                    {week.title && (
                        <h4 className="text-lg sm:text-xl font-bold mb-2" style={{ color: "#0f172a" }}>
                            {week.title}
                        </h4>
                    )}

                    {week.description && (
                        <p className="text-sm sm:text-[15px] m-0" style={{ color: "#334155", lineHeight: 1.6 }}>
                            {week.description}
                        </p>
                    )}

                    <div className="mt-auto pt-4 md:pt-5 flex items-center justify-between gap-3">
                        {exerciseCount > 0 && (
                            <span className="px-3 py-1.5 rounded-full text-xs font-semibold" style={{
                                background: "rgba(255, 255, 255, 0.6)",
                                color: "#0f172a"
                            }}>
                                {exerciseCount} {exerciseCount === 1 ? singularLabel : pluralLabel}
                            </span>
                        )}
                        <span className="text-sm font-semibold" style={{ color: "#0f172a" }}>
                            Explore →
                        </span>
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link
            to={`/strong-healthy/${programId}/${week.id}`}
            className="no-underline"
        >
            <div
                className="bg-white rounded-xl p-4 transition-all duration-300 hover:scale-[1.01] hover:shadow-lg cursor-pointer"
                style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        {week.title && (
                            <h4 className="font-semibold mb-1" style={{ color: '#1e293b' }}>
                                {week.title}
                            </h4>
                        )}
                        {week.description && (
                            <p className="text-sm m-0" style={{ color: '#64748b' }}>
                                {week.description}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                        {exerciseCount > 0 && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium" style={{
                                background: 'rgba(99, 102, 241, 0.1)',
                                color: '#6366f1'
                            }}>
                                {exerciseCount} {exerciseCount === 1 ? singularLabel : pluralLabel}
                            </span>
                        )}
                        <span style={{ color: '#94a3b8', fontSize: '1.25rem' }}>→</span>
                    </div>
                </div>
            </div>
        </Link>
    );
}
